import { useContext, useState } from "react";
import styled, { keyframes } from "styled-components";
import { Link, useNavigate } from "react-router-dom";
import { ProductContext } from "../../../context/ProductContext";
import { useNavigation } from "../../../utils/navigation";
import { AuthUserContext } from "../../../context/AuthUserContext";
import { useTranslation } from "react-i18next";
import { getCategoryImage } from "../../../utils/categoryImages";

const slideDown = keyframes`
  from {
    opacity: 0;
    transform: translateY(-6px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const Container = styled.div`
  width: 100%;
  background-color: var(--bg-200);
  overflow-y: auto;
  height: calc(100vh - 100px);
  display: flex;
  flex-direction: column;
`;
const Wrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 100%;
`;
const GridContainer = styled.div`
  min-width: 100%;
  height: 100%;
  min-height: 100%;
  overflow-x: hidden;
  position: relative;
  padding: var(--spacing-md) 0;
  display: flex;
  flex-direction: column;
  gap: var(--spacing-md);
`;

const NavCard = styled.div`
  margin: 0 var(--spacing-md);
  background-color: var(--bg-100);
  border-radius: var(--border-radius-large);
  overflow: hidden;
`;
const NavRow = styled.button`
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  width: 100%;
  padding: 14px var(--spacing-md);
  background: none;
  border: none;
  border-bottom: 1px solid var(--bg-200);
  color: var(--text-100);
  text-align: left;
  transition: background-color 0.15s ease-in-out;

  &:last-of-type {
    border-bottom: none;
  }
  &:hover,
  &:focus-visible {
    background-color: var(--bg-200);
  }
`;
const NavRowIcon = styled.div`
  width: 20px;
  height: 20px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--text-200);
`;
const NavLabel = styled.span`
  flex: 1;
  font-size: var(--font-size-base);
  font-weight: 500;
`;
const Chevron = styled.svg`
  transition: transform 0.25s ease;
  transform: ${({ $isOpen }) => ($isOpen ? "rotate(90deg)" : "rotate(0)")};
`;

/* --- Brands accordion (slide-down grid) --- */
const AccordionPanel = styled.div`
  overflow: hidden;
  max-height: ${({ $isOpen }) => ($isOpen ? "2000px" : "0px")};
  transition: max-height 0.35s ease;
  background-color: var(--bg-200);
`;
const BrandGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: var(--spacing-sm);
  padding: var(--spacing-sm) var(--spacing-md) var(--spacing-md);
`;
const BrandTile = styled(Link)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: var(--spacing-sm) 4px;
  text-decoration: none;
  color: var(--text-100);
  background-color: var(--bg-100);
  border-radius: var(--border-radius-base);
  animation: ${slideDown} 0.2s ease both;
  text-align: center;

  &:hover,
  &:active {
    background-color: var(--bg-300);
  }
`;
const BrandIcon = styled.div`
  width: 34px;
  height: 34px;
  min-width: 34px;
  border-radius: 50%;
  background-color: ${({ color }) => color || "var(--bg-200)"};
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
`;
const BrandIconImg = styled.img`
  width: 70%;
  height: 70%;
  object-fit: contain;
`;
const BrandName = styled.span`
  font-size: var(--font-size-small);
  line-height: 1.2;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
`;

/* --- Account card --- */
const AccountCard = styled.div`
  margin: 0 var(--spacing-md);
  display: flex;
  flex-direction: column;
  border-radius: var(--border-radius-large);
  background-color: var(--accent-200);
  overflow: hidden;
`;
const AccountGreeting = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  color: var(--bg-100);
  padding: var(--spacing-md);
`;
const AccountAvatar = styled.div`
  width: 42px;
  height: 42px;
  min-width: 42px;
  border-radius: 50%;
  background-color: var(--bg-100);
  color: var(--accent-200);
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: var(--font-size-large);
  text-transform: uppercase;
`;
const AccountNameBlock = styled.div`
  min-width: 0;
`;
const AccountName = styled.div`
  font-weight: 600;
  font-size: var(--font-size-base);
  text-transform: capitalize;
`;
const AccountEmail = styled.div`
  font-size: var(--font-size-small);
  color: rgba(255, 255, 255, 0.7);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
const AccountLinks = styled.div`
  background-color: var(--bg-100);
  border-radius: var(--border-radius-large) var(--border-radius-large) 0 0;
  padding: 4px var(--spacing-md) 0;
`;
const AccountLink = styled.button`
  text-decoration: none;
  color: var(--text-100);
  background-color: transparent;
  border: none;
  padding: 14px 0;
  display: flex;
  gap: var(--spacing-sm);
  align-items: center;
  width: 100%;
  font-size: var(--font-size-base);
  text-align: left;
  border-bottom: 1px solid var(--bg-200);
  transition: background-color 0.15s ease-in-out;

  &:last-of-type {
    border-bottom: none;
  }
  &:hover,
  &:focus-visible {
    background-color: var(--bg-200);
  }
`;
const AccountLinkIcon = styled.div`
  width: 20px;
  height: 20px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--text-200);
`;
const AccountLinkLabel = styled.span`
  flex: 1;
`;
const LogoutRow = styled(AccountLink)`
  color: var(--error-color);
  border-bottom: none;

  ${AccountLinkIcon} {
    color: var(--error-color);
  }
`;

/* --- Guest CTA --- */
const GuestCard = styled.div`
  margin: 0 var(--spacing-md);
  padding: var(--spacing-lg) var(--spacing-md);
  border-radius: var(--border-radius-large);
  background-color: var(--bg-100);
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 4px;
`;
const GuestTitle = styled.p`
  margin: 0;
  font-weight: 600;
  font-size: var(--font-size-base);
  color: var(--text-100);
`;
const GuestSubtitle = styled.p`
  margin: 0 0 var(--spacing-sm);
  font-size: var(--font-size-small);
  color: var(--text-200);
`;
const ButtonShell = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  width: 100%;
`;
const TButton = styled.button`
  margin-top: 10px;
  width: 100%;
`;
const LButton = styled(TButton)`
  background-color: var(--bg-200);
  border: none;
  color: var(--text-100);

  &:hover,
  &:active,
  &:focus {
    background-color: var(--bg-300);
  }
`;

const HomeRow = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-xs);
  margin: var(--spacing-sm) var(--spacing-md) 0;
  padding: var(--spacing-sm);
  background: none;
  border: none;
  color: var(--text-200);
  font-size: var(--font-size-small);
  text-transform: uppercase;
  letter-spacing: 0.04em;

  &:hover,
  &:focus-visible {
    color: var(--primary-100);
  }
`;

const ConfirmContainer = styled.div`
  background-color: #00000031;
  position: fixed;
  left: 0;
  top: 0;
  z-index: 999999;
  width: 100vw;
  height: 100vh;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
`;
const ConfirmWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  flex-direction: column;
  background-color: var(--bg-200);
  color: var(--text-200);
  position: relative;
  padding: 40px 24px;
  width: 90%;
  border-radius: var(--border-radius-large);
  text-align: center;
  @media (min-width: 768px) {
    width: 400px;
  }
`;
const ConfirmButtonRow = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: var(--spacing-sm);
  margin-top: var(--spacing-lg);
`;
const ConfirmButtonCancel = styled.button`
  padding: var(--spacing-sm) var(--spacing-lg);
  color: var(--text-200);
  background-color: var(--bg-100);
  transition: background-color 0.15s ease-in-out;

  &:hover,
  &:active,
  &:focus {
    background-color: var(--bg-300);
  }
`;
const ConfirmButtonLogout = styled(ConfirmButtonCancel)`
  color: var(--error-color);
  border-color: var(--error-color);

  &:hover,
  &:active,
  &:focus {
    background-color: var(--error-color);
    color: var(--bg-100);
  }
`;

const PouchIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
    <path
      d="M7 3h10a2 2 0 0 1 2 2v6a7 7 0 0 1-14 0V5a2 2 0 0 1 2-2Z"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M9 21h6"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
    />
  </svg>
);
const StarIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
    <path
      d="M12 3.5l2.6 5.4 5.9.7-4.3 4.2 1 5.9-5.2-2.8-5.2 2.8 1-5.9-4.3-4.2 5.9-.7L12 3.5Z"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinejoin="round"
    />
  </svg>
);
const TagIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
    <path
      d="M12.7 3.3 20 10.6a2 2 0 0 1 0 2.8l-6.6 6.6a2 2 0 0 1-2.8 0L3.9 13.3A2 2 0 0 1 3.3 12l.4-6.7a2 2 0 0 1 1.9-1.9L12 3a2 2 0 0 1 .7.3Z"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinejoin="round"
    />
    <circle cx="9" cy="9" r="1.4" fill="currentColor" />
  </svg>
);
const ProfileIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
    <path
      d="M12 12a4.5 4.5 0 1 0 0-9 4.5 4.5 0 0 0 0 9ZM4 20.5c0-3.6 3.6-6.5 8-6.5s8 2.9 8 6.5"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);
const OrdersIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
    <path
      d="M4 7h16M6 7l1 12a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2l1-12M9 7V5a3 3 0 0 1 6 0v2"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);
const AddressIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
    <path
      d="M12 21s7-6.1 7-11.5A7 7 0 0 0 5 9.5C5 14.9 12 21 12 21Z"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <circle cx="12" cy="9.5" r="2.3" stroke="currentColor" strokeWidth="1.7" />
  </svg>
);
const LogoutIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
    <path
      d="M9 21H6a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h3M16 17l5-5-5-5M21 12H9"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);
const HamburgerMenuData = ({ setIsOpen }) => {
  const { t } = useTranslation();
  const { categories } = useContext(ProductContext);
  const { authTokens, userProfile, logoutUser } = useContext(AuthUserContext);
  const navigate = useNavigate();
  const {
    goToCategory,
    goToHome,
    goToShop,
    goToBestSeller,
    goToLogin,
    goToRegister,
    goToProfile,
    goToOrderHistory,
    goToAddressBook,
  } = useNavigation();

  const [isBrandsOpen, setIsBrandsOpen] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);

  const toggleMenu = () => {
    setIsOpen(false);
  };
  const toggleCategory = (event, category) => {
    event.preventDefault();
    navigate(goToCategory(category.name));
    toggleMenu();
  };

  return (
    <Container>
      <Wrapper>
        <GridContainer>
          <NavCard>
            <NavRow
              onClick={() => {
                navigate(goToShop());
                toggleMenu();
              }}
            >
              <NavRowIcon>
                <PouchIcon />
              </NavRowIcon>
              <NavLabel>{t("MENU.NICOTINE_POUCHES")}</NavLabel>
            </NavRow>

            <NavRow
              onClick={() => {
                navigate(goToBestSeller());
                toggleMenu();
              }}
            >
              <NavRowIcon>
                <StarIcon />
              </NavRowIcon>
              <NavLabel>{t("MENU.BEST_SELLERS")}</NavLabel>
            </NavRow>

            <NavRow onClick={() => setIsBrandsOpen((prev) => !prev)}>
              <NavRowIcon>
                <TagIcon />
              </NavRowIcon>
              <NavLabel>{t("MENU.BRANDS")}</NavLabel>
              <Chevron
                $isOpen={isBrandsOpen}
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
              >
                <path
                  d="M9 6l6 6-6 6"
                  stroke="var(--text-200)"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </Chevron>
            </NavRow>
            <AccordionPanel $isOpen={isBrandsOpen}>
              <BrandGrid>
                {categories &&
                  categories.length > 0 &&
                  categories.slice(0, 100).map((category, index) => (
                    <BrandTile
                      key={index}
                      to="#"
                      onClick={(event) => toggleCategory(event, category)}
                    >
                      <BrandIcon color={category.color}>
                        {getCategoryImage(category.slug) && (
                          <BrandIconImg
                            src={getCategoryImage(category.slug)}
                            alt=""
                            aria-hidden="true"
                          />
                        )}
                      </BrandIcon>
                      <BrandName>{category.name}</BrandName>
                    </BrandTile>
                  ))}
              </BrandGrid>
            </AccordionPanel>
          </NavCard>

          {authTokens ? (
            <AccountCard>
              <AccountGreeting>
                <AccountAvatar>
                  {(userProfile?.first_name || "").charAt(0)}
                </AccountAvatar>
                <AccountNameBlock>
                  <AccountName>
                    {userProfile?.first_name || t("MENU.ACCOUNT")}
                  </AccountName>
                  {userProfile?.email && (
                    <AccountEmail>{userProfile.email}</AccountEmail>
                  )}
                </AccountNameBlock>
              </AccountGreeting>
              <AccountLinks>
                <AccountLink
                  onClick={() => {
                    navigate(goToProfile());
                    toggleMenu();
                  }}
                >
                  <AccountLinkIcon>
                    <ProfileIcon />
                  </AccountLinkIcon>
                  <AccountLinkLabel>
                    {t("PROFILE.PROFILE_INFO")}
                  </AccountLinkLabel>
                </AccountLink>
                <AccountLink
                  onClick={() => {
                    navigate(goToOrderHistory());
                    toggleMenu();
                  }}
                >
                  <AccountLinkIcon>
                    <OrdersIcon />
                  </AccountLinkIcon>
                  <AccountLinkLabel>{t("PROFILE.ORDERS")}</AccountLinkLabel>
                </AccountLink>
                <AccountLink
                  onClick={() => {
                    navigate(goToAddressBook());
                    toggleMenu();
                  }}
                >
                  <AccountLinkIcon>
                    <AddressIcon />
                  </AccountLinkIcon>
                  <AccountLinkLabel>
                    {t("PROFILE.MANAGE_ADDRESSES")}
                  </AccountLinkLabel>
                </AccountLink>
                <LogoutRow onClick={() => setShowLogoutConfirm(true)}>
                  <AccountLinkIcon>
                    <LogoutIcon />
                  </AccountLinkIcon>
                  <AccountLinkLabel>{t("PROFILE.LOGOUT")}</AccountLinkLabel>
                </LogoutRow>
              </AccountLinks>
            </AccountCard>
          ) : (
            <GuestCard>
              <GuestTitle>{t("LOGIN.TITLE")}</GuestTitle>
              <GuestSubtitle>{t("LOGIN.SUBTITLE")}</GuestSubtitle>
              <ButtonShell>
                <TButton
                  onClick={() => {
                    navigate(goToLogin());
                    toggleMenu();
                  }}
                >
                  {t("MENU.LOGIN")}
                </TButton>
                <LButton
                  onClick={() => {
                    navigate(goToRegister());
                    toggleMenu();
                  }}
                >
                  {t("MENU.SIGN_UP")}
                </LButton>
              </ButtonShell>
            </GuestCard>
          )}

          <HomeRow
            onClick={() => {
              navigate(goToHome());
              toggleMenu();
            }}
          >
            {t("ARIA.HOME")}
          </HomeRow>
        </GridContainer>
      </Wrapper>
      {showLogoutConfirm && (
        <ConfirmContainer>
          <ConfirmWrapper>
            {t("PROFILE.CONFIRM_LOGOUT")}
            <ConfirmButtonRow>
              <ConfirmButtonLogout
                className="button-transparent"
                onClick={() => {
                  logoutUser();
                  setShowLogoutConfirm(false);
                  toggleMenu();
                }}
              >
                {t("BUTTONS.LOGOUT")}
              </ConfirmButtonLogout>
              <ConfirmButtonCancel
                className="button-transparent"
                onClick={() => setShowLogoutConfirm(false)}
              >
                {t("BUTTONS.CANCEL")}
              </ConfirmButtonCancel>
            </ConfirmButtonRow>
          </ConfirmWrapper>
        </ConfirmContainer>
      )}
    </Container>
  );
};

export default HamburgerMenuData;
